import { QUESTION_RESPONSE_STATUS, QUESTION_TYPES } from '../constants';

/*
 * Choice groups — "attempt any X of these Y questions".
 *
 * A group lives on a section (`section.choiceGroups`) and names a subset of that section's
 * questions, all worth the same marks — the teacher sets `marksPerQuestion` on the group
 * itself. Nothing is locked: the student may answer as many of them as they like, and
 * grading keeps their best `requiredCount` answers at the marks those answers earned. The
 * block is therefore worth `requiredCount * marksPerQuestion`.
 *
 * A reading comprehension passage can be one whole alternative — "answer this block or that
 * question". The student never opens the passage itself, they answer its sub-questions, so
 * everything here resolves a sub-question up to its passage before asking which group it is
 * in, and treats the block as attempted once any one of its sub-questions is answered.
 *
 * So everything here is about telling the student what they have to do and how far along
 * they are — never about stopping them.
 *
 * Mirrors classwise-backend/src/helpers/choice-group.helper.js.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Every group on the assignment, each tagged with the section it belongs to. */
export const getAllChoiceGroups = (assignment) =>
  asArray(assignment?.sections).flatMap((section) =>
    asArray(section?.choiceGroups).map((group) => ({ ...group, sectionId: section.id }))
  );

/** The passage a sub-question belongs to, or null for a top-level question. */
export const findParentQuestion = (assignment, questionId) => {
  const id = String(questionId);
  return (
    asArray(assignment?.questions).find((question) =>
      asArray(question?.subQuestions).some((subId) => String(subId) === id)
    ) || null
  );
};

/**
 * The group that names this question, or null. A question is in at most one group.
 *
 * Deliberately does NOT look at the question's passage. When a passage is an alternative,
 * the choice is about the block as a whole, not about each question inside it — labelling
 * every sub-question "attempt any 1 of 2" reads as though those sub-questions were the
 * alternatives. `getChoiceGroupForPassage` carries that block-level fact instead.
 */
export const getChoiceGroupForQuestion = (assignment, questionId) => {
  if (!questionId) return null;
  const id = String(questionId);
  return (
    getAllChoiceGroups(assignment).find((group) =>
      asArray(group.questions).some((memberId) => String(memberId) === id)
    ) || null
  );
};

/**
 * The group a question's reading-comprehension passage is an alternative in, or null.
 *
 * The student answers sub-questions, never the passage, so this is how "the block you are
 * working in is optional" reaches them — shown once for the block rather than per question.
 */
export const getChoiceGroupForPassage = (assignment, questionId) => {
  const parent = findParentQuestion(assignment, questionId);
  if (!parent) return null;
  return getChoiceGroupForQuestion(assignment, parent.id);
};

/**
 * The group whose outcome explains this question's marks: its own, or its passage's when the
 * whole block was the alternative. Results and review need this — a sub-question of a
 * displaced passage scores nothing, and that has to be explicable.
 */
export const getEffectiveChoiceGroup = (assignment, questionId) =>
  getChoiceGroupForQuestion(assignment, questionId) ||
  getChoiceGroupForPassage(assignment, questionId);

/**
 * Whether one member of a group has been attempted. A passage counts as attempted as soon as
 * the student answers any question about it — they have committed to that alternative.
 */
const isMemberAnswered = (assignment, memberId, answeredIds) => {
  if (answeredIds.has(String(memberId))) return true;

  const member = asArray(assignment?.questions).find(
    (question) => String(question?.id) === String(memberId)
  );
  return asArray(member?.subQuestions).some((subId) => answeredIds.has(String(subId)));
};

/** Ids of every question inside some choice group. */
export const getGroupedQuestionIds = (assignment) => {
  const ids = new Set();
  getAllChoiceGroups(assignment).forEach((group) => {
    asArray(group.questions).forEach((questionId) => ids.add(String(questionId)));
  });
  return ids;
};

/**
 * How far the student has got with one group.
 *
 * `answered` counts every member they have answered, which may legitimately exceed
 * `requiredCount` — answering more is allowed, and the extra answers simply do not count.
 */
export const getChoiceGroupProgress = (group, studentResponse = [], assignment = null) => {
  const memberIds = asArray(group?.questions).map(String);

  const answeredIds = new Set(
    asArray(studentResponse)
      .filter((response) => response?.status === QUESTION_RESPONSE_STATUS.ANSWERED)
      .map((response) => String(response.questionId))
  );

  const answered = memberIds.filter((memberId) =>
    isMemberAnswered(assignment, memberId, answeredIds)
  ).length;

  const requiredCount = Number(group?.requiredCount) || 0;

  return {
    answered,
    requiredCount,
    total: memberIds.length,
    remaining: Math.max(0, requiredCount - answered),
    isSatisfied: answered >= requiredCount,
  };
};

/**
 * Groups the student has not answered enough of yet — what the end-of-test warning lists.
 *
 * A rule among a passage's questions is left out while that passage is an alternative the
 * student has not started: they may be answering the other alternative instead, and the rule
 * offering the passages already says whether they have done enough.
 */
export const getUnsatisfiedChoiceGroups = (assignment, studentResponse = []) => {
  const answeredIds = new Set(
    asArray(studentResponse)
      .filter((response) => response?.status === QUESTION_RESPONSE_STATUS.ANSWERED)
      .map((response) => String(response.questionId))
  );

  return getAllChoiceGroups(assignment)
    .filter((group) => {
      const passage = findParentQuestion(assignment, asArray(group?.questions)[0]);
      if (!passage || !getChoiceGroupForQuestion(assignment, passage.id)) return true;
      return isMemberAnswered(assignment, passage.id, answeredIds);
    })
    .map((group) => ({
      group,
      progress: getChoiceGroupProgress(group, studentResponse, assignment),
    }))
    .filter(({ progress }) => !progress.isSatisfied);
};

const isPassage = (question) =>
  question?.type === QUESTION_TYPES.READING_COMPREHENSION ||
  asArray(question?.subQuestions).length > 0;

/**
 * How many questions the student has to answer - the count the start screen shows.
 *
 * Counted the way computeSolvableQuestionCount counts them in assignment-fe
 * (canvas/redux/choice-group.js): an X-of-Y rule contributes its `requiredCount`, not every
 * alternative; a passage counts through its questions rather than as one of its own; and a
 * passage that is itself an alternative is covered by its rule - a rule among its questions
 * included - so none of it counts twice.
 */
export const getQuestionsToAnswerCount = (assignment) => {
  const questions = asArray(assignment?.questions);
  const grouped = getGroupedQuestionIds(assignment);

  const coveredByGroupedPassage = new Set();
  questions.forEach((question) => {
    if (!grouped.has(String(question?.id))) return;
    asArray(question?.subQuestions).forEach((id) => coveredByGroupedPassage.add(String(id)));
  });

  const ungrouped = questions.reduce((count, question) => {
    const id = String(question?.id);
    if (grouped.has(id) || coveredByGroupedPassage.has(id)) return count;
    if (isPassage(question)) return count;
    return count + 1;
  }, 0);

  const fromRules = getAllChoiceGroups(assignment)
    .filter(
      (group) => !asArray(group?.questions).some((id) => coveredByGroupedPassage.has(String(id)))
    )
    .reduce((count, group) => count + (Number(group?.requiredCount) || 0), 0);

  return ungrouped + fromRules;
};

/** What the block is worth: the marks of one question, times how many must be attempted. */
export const getChoiceGroupTotal = (group) =>
  Math.round(
    (Number(group?.marksPerQuestion) || 0) * (Number(group?.requiredCount) || 0) * 100
  ) / 100;

/** "Attempt any 2 of 3" — the same phrasing the teacher sees while building the paper. */
export const describeChoiceGroup = (group) =>
  group ? `Attempt any ${group.requiredCount} of ${asArray(group.questions).length}` : '';
